import { useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Alert, Input } from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { AlertTriangle, Eye, EyeOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { StepWrapper } from '@/common/components/ConnectionSteps/StepWrapper'
import {
  ServiceSolutionsValidationForm,
  ServiceSolutionsValidationSchema,
} from '../../schemas/ChangePasswordSchema'

type ValidationStepProps = {
  appData: {
    applicationAccountUserId: string
    applicationName: string
    accountName: string
  }
  stepInfo: {
    current: number
    total: number
  }
  onValidationChange?: (isValid: boolean) => void
  onFormChange?: (values: unknown) => void
  context?: 'change_password' | 'remove_access' | 'disconnect'
  isLastStep?: boolean
}

export function ValidationStep({
  appData,
  stepInfo,
  onValidationChange,
  onFormChange,
  context = 'change_password',
  isLastStep = false,
}: ValidationStepProps) {
  const { t } = useTranslation(['common', 'profile'])
  const [showServiceSolutionsPassword, setShowServiceSolutionsPassword] =
    useState(false)

  const getDisplayName = () => {
    if (appData.accountName && appData.accountName.trim()) {
      return `${appData.applicationName} (${appData.accountName})`
    }
    return appData.applicationName
  }

  const getDescription = () => {
    const displayName = getDisplayName()

    switch (context) {
      case 'change_password':
        return t(
          'profile:access.modal.change_password.validation.description',
          {
            displayName,
          },
        )
      case 'disconnect':
        return t('profile:access.modal.disconnect.validation.description', {
          displayName,
        })
      case 'remove_access':
        return t('profile:access.modal.remove_access.validation.description', {
          displayName,
        })
      default:
        return t(
          'profile:access.modal.change_password.validation.description',
          {
            displayName,
          },
        )
    }
  }

  const hookForm = useForm<ServiceSolutionsValidationForm>({
    resolver: zodResolver(ServiceSolutionsValidationSchema()),
    mode: 'onChange',
    defaultValues: {
      serviceSolutionsPassword: '',
    },
  })

  const isFormValid = hookForm.formState.isValid
  const formValues = hookForm.watch()

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isFormValid)
    }
  }, [isFormValid, onValidationChange])

  useEffect(() => {
    if (onFormChange) {
      onFormChange(formValues)
    }
  }, [formValues, onFormChange])

  return (
    <FormProvider {...hookForm}>
      <StepWrapper
        appData={{
          applicationId: '',
          applicationName: appData.applicationName,
          userId: '',
        }}
        stepInfo={stepInfo}
        accountName={appData.accountName}
      >
        <div className="space-y-4">
          <div className="text-sm text-gray-600 mb-4">{getDescription()}</div>

          <div className="w-[50%]">
            <Input.Icon
              size="lg"
              name="serviceSolutionsPassword"
              label={t(
                'app_connection.passwords.existing_user.service_solutions_label',
              )}
              type={showServiceSolutionsPassword ? 'text' : 'password'}
              placeholder={t(
                'app_connection.passwords.existing_user.service_solutions_placeholder',
              )}
              icon={
                showServiceSolutionsPassword ? (
                  <Eye
                    className="text-base-500 mt-2"
                    onClick={() =>
                      setShowServiceSolutionsPassword(
                        !showServiceSolutionsPassword,
                      )
                    }
                  />
                ) : (
                  <EyeOff
                    className="text-base-500 mt-2"
                    onClick={() =>
                      setShowServiceSolutionsPassword(
                        !showServiceSolutionsPassword,
                      )
                    }
                  />
                )
              }
              iconPosition="right"
            />
          </div>

          {context === 'change_password' && (
            <Alert.Root>
              <Alert.Icon icon={<AlertTriangle />} />
              <Alert.Body>
                <Alert.Description>
                  {t(
                    'profile:access.modal.change_password.validation.info_message',
                  )}
                </Alert.Description>
              </Alert.Body>
            </Alert.Root>
          )}

          {context === 'remove_access' && isLastStep && (
            <Alert.Root>
              <Alert.Icon icon={<AlertTriangle />} />
              <Alert.Body>
                <Alert.Description>
                  {t(
                    'profile:access.modal.remove_access.type_selection.warning_message',
                  )}
                </Alert.Description>
              </Alert.Body>
            </Alert.Root>
          )}
        </div>
      </StepWrapper>
    </FormProvider>
  )
}
